import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { FaFileCode } from 'react-icons/fa';
import {
  DISCREPANCY_MISMATCH_REASON_LABELS,
  DISCREPANCY_MISMATCH_REASON_COLORS,
} from '../../../constants/discrepancyMismatchReasons';
import {
  DISCREPANCY_MAPPING_STATUS_LABELS,
  DISCREPANCY_MAPPING_STATUS_COLORS,
} from '../../../constants/discrepancyMappingStatuses';
import {
  DISCREPANCY_RADAR_ONLY_REASON_LABELS,
  DISCREPANCY_RADAR_ONLY_REASON_COLORS,
  DISCREPANCY_RADAR_ONLY_REASON_DESCRIPTIONS,
} from '../../../constants/discrepancyRadarOnly';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { TwoLine, TwoLinePrimary, TwoLineSecondary, TableWrapper } from './styles';
import { getChipSx } from './chipStyles';
import { toLocalDate } from '../../../utils/dateOnly';

// Lightweight cell for manager (radar or reported). Falls back to '—' when nothing
// useful to show. radarManager has { id, azureId, email, fullName }; reportedManager
// has { externalId, email, name, radarId }. The shape difference is intentional —
// reported is upstream-flavored, not a radar Employee.
const ManagerCell = ({ manager }) => {
  if (!manager) {
    return <span style={{ color: '#999' }}>—</span>;
  }
  const displayName = manager.fullName || manager.name;
  return (
    <TwoLine>
      <TwoLinePrimary>{displayName || '—'}</TwoLinePrimary>
      {manager.email && <TwoLineSecondary>{manager.email}</TwoLineSecondary>}
      {manager.externalId && (
        <TwoLineSecondary title="Upstream external ID">id: {manager.externalId}</TwoLineSecondary>
      )}
    </TwoLine>
  );
};

const Dash = () => <span style={{ color: '#999' }}>—</span>;

const fmtDate = (value) => {
  if (!value) return null;
  try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
};

const SOURCE_LABELS = { ENOVA: 'Enova', AMERICAS: 'Americas' };

// ADR-130: reason chip with its explanation.
const RadarOnlyReasonCell = ({ row }) => {
  const reason = row.radarOnlyReason;
  if (!reason) return <Dash />;
  const tooltip = DISCREPANCY_RADAR_ONLY_REASON_DESCRIPTIONS[reason];
  return (
    <Tooltip title={tooltip || ''} arrow>
      <Chip
        size="small"
        label={DISCREPANCY_RADAR_ONLY_REASON_LABELS[reason] || reason}
        sx={getChipSx(DISCREPANCY_RADAR_ONLY_REASON_COLORS[reason])}
      />
    </Tooltip>
  );
};

/**
 * @param {Object} props
 * @param {Array} props.rows
 * @param {Function} props.onNavigateToEmployee
 * @param {'compare'|'radarOnly'} [props.mode='compare'] - ADR-130: Radar only rows get their own columns
 * @param {Function} [props.onViewSourceRecord] - ADR-131: when given, rows with a stored feed record offer "View source record"
 */
const DiscrepancyTable = ({
  rows, onNavigateToEmployee, mode = 'compare', onViewSourceRecord,
}) => {
  const isRadarOnly = mode === 'radarOnly';
  // Radar only rows were not in the feed, so they never have a source record:
  // the Actions column is shown only in the comparison views.
  const showActions = Boolean(onViewSourceRecord) && !isRadarOnly;

  const renderActions = (row) => (
    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
      {onViewSourceRecord && row.hasSourceRecord && (
        <Tooltip title="View source record (feed data from the audit day)" arrow>
          <IconButton size="small" onClick={() => onViewSourceRecord(row)} aria-label="View source record">
            <FaFileCode size={14} />
          </IconButton>
        </Tooltip>
      )}
    </TableCell>
  );
  const handleNavigateToEmployee = (employee) => {
    if (!employee?.id) return;
    onNavigateToEmployee?.(employee);
  };

  return (
    <TableWrapper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            {isRadarOnly ? (
              <TableRow>
                <TableCell>Consultant</TableCell>
                <TableCell>Radar representative</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Hire date</TableCell>
                <TableCell>Engagement / company</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Last seen</TableCell>
                {showActions && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            ) : (
              <TableRow>
                <TableCell>Consultant</TableCell>
                <TableCell>Radar representative</TableCell>
                <TableCell>Reported representative</TableCell>
                <TableCell>Mismatch reason</TableCell>
                <TableCell>Mapping status</TableCell>
                {showActions && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  transition: 'background-color 0.15s',
                  '&:hover': { backgroundColor: '#f8fafc' },
                }}
              >
                <TableCell>
                  {row.employee ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleNavigateToEmployee(row.employee)}
                      title="View individual"
                    >
                      <EmployeeAvatar employee={row.employee} size={32} />
                      <TwoLine>
                        <TwoLinePrimary>{row.employee.fullName || '—'}</TwoLinePrimary>
                        {row.employee.email && (
                          <TwoLineSecondary>{row.employee.email}</TwoLineSecondary>
                        )}
                      </TwoLine>
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </TableCell>
                <TableCell>
                  <ManagerCell manager={row.radarManager} />
                </TableCell>
                {isRadarOnly ? (
                  <>
                    <TableCell>{row.employee?.cluster?.name || <Dash />}</TableCell>
                    <TableCell>{toLocalDate(row.employee?.hireDate)?.toLocaleDateString() || <Dash />}</TableCell>
                    <TableCell>
                      <TwoLine>
                        <TwoLinePrimary>{row.employee?.engagementModel?.name || '—'}</TwoLinePrimary>
                        {row.employee?.contractCompany?.name && (
                          <TwoLineSecondary>{row.employee.contractCompany.name}</TwoLineSecondary>
                        )}
                      </TwoLine>
                    </TableCell>
                    <TableCell><RadarOnlyReasonCell row={row} /></TableCell>
                    <TableCell>
                      {row.lastSeenAt ? (
                        <TwoLine>
                          <TwoLinePrimary>{fmtDate(row.lastSeenAt)}</TwoLinePrimary>
                          <TwoLineSecondary>
                            {SOURCE_LABELS[row.lastSeenSourceSystem] || row.lastSeenSourceSystem}
                          </TwoLineSecondary>
                        </TwoLine>
                      ) : <Dash />}
                    </TableCell>
                  </>
                ) : (
                  <TableCell>
                    <ManagerCell manager={row.reportedManager} />
                  </TableCell>
                )}
                {!isRadarOnly && (
                <TableCell>
                  {row.mismatchReason ? (
                    <Chip
                      size="small"
                      label={DISCREPANCY_MISMATCH_REASON_LABELS[row.mismatchReason] || row.mismatchReason}
                      sx={getChipSx(DISCREPANCY_MISMATCH_REASON_COLORS[row.mismatchReason])}
                    />
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </TableCell>
                )}
                {!isRadarOnly && (
                <TableCell>
                  <Chip
                    size="small"
                    label={DISCREPANCY_MAPPING_STATUS_LABELS[row.mappingStatus] || row.mappingStatus}
                    sx={getChipSx(DISCREPANCY_MAPPING_STATUS_COLORS[row.mappingStatus])}
                  />
                </TableCell>
                )}
                {showActions && renderActions(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableWrapper>
  );
};

export default DiscrepancyTable;
